import { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import { IconLibrary, PermissionButton } from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import {
  UserResetMFADialogWithButton,
  UserSendPasswordResetEmailDialogWithButton,
  UserUpdatePasswordDialogWithButton,
} from '@product-framework/components/Dialogs';
import { useServiceModule } from '@product-framework/components/ServiceModuleProvider';
import schema from './edit.json';
import validationSchema from './validations';

const FORM_ID = 'ServiceProviderUserEditForm';
const PERMISSION_SP = 'SERVICEPROVIDERUSER';

function UserEdit({ userId, onChange = () => undefined }) {
  const { formatMessage } = useLocalization();

  const formRef = useRef();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, getApiUrl } = useDataProvider(provider);
  const [initialValues, setInitialValues] = useState({});
  const { moduleSettings } = useServiceModule();

  const fetchDefaultValues = useCallback(async () => {
    const url = `${getApiUrl()}/sxcl/v1/users/${userId}`;

    const [response] = await Promise.all([get(url)]);

    if (response?.ok) {
      const result = {
        data: {
          ...response.data,
          role: {
            value: response?.data?.role?.id,
            label: response?.data?.role?.roleIdentifier,
          },
          insightPurchased: moduleSettings.insightPurchased || false,
        },
        errors: null,
      };
      setInitialValues(result.data);
      return result;
    }

    let errors = [];
    if (response?.errors) {
      errors = [...response.errors];
    }

    return {
      data: null,
      errors,
    };
  }, [userId]);

  const onSubmit = async formData => {
    const { disabledReason, ...data } = formData;
    const url = `${getApiUrl()}/sxcl/v1/users/${userId}`;
    const requestData = {
      email: data?.email,
      firstName: data?.firstName,
      lastName: data?.lastName,
      enabled: data?.enabled === 'true',
      userRoleId: data?.role?.value,
      phoneNumber: data?.phoneNumber,
      phoneNumberType: data?.phoneNumberType,
      smsOptedIn: [true, 'true'].includes(data?.smsOptedIn),
    };
    const response = await put(url, requestData);

    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_CREATE_SUCCESS',
          defaultMessage: 'User was saved successfully',
        }),
      );

      const nextValues = {
        ...response.data,
        role: {
          value: response.data.role.id,
          label: response.data.role.roleIdentifier,
        },
        insightPurchased: moduleSettings.insightPurchased,
      };

      return {
        nextValues,
        errors: null,
        callback: () => {
          setInitialValues(nextValues);
          onChange();
        },
      };
    }
    return {
      nextValues: data,
      errors: response.errors,
    };
  };

  return (
    <Form
      formId={FORM_ID}
      ref={formRef}
      permissionEntities={[PERMISSION_SP]}
      onSubmit={onSubmit}
      schema={schema}
      fetchDefaultValues={fetchDefaultValues}
      validationSchema={validationSchema(formatMessage, getApiUrl, get)}>
      <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
        <PermissionButton
          permissionName="CAN_UPDATE_SP_USER"
          variant="contained"
          startIcon={<IconLibrary.Save />}
          color="primary"
          onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
          {formatMessage({
            id: 'USER_CREATE_SAVE_BUTTON',
            defaultMessage: 'Save User',
          })}
        </PermissionButton>
        <PermissionButton
          permissionName="CAN_UPDATE_SP_USER"
          variant="contained"
          color="primary"
          startIcon={<IconLibrary.Reset />}
          onClick={() => formRef.current?.reset()}>
          {formatMessage({
            id: 'USER_CREATE_RESET_BUTTON',
            defaultMessage: 'Reset',
          })}
        </PermissionButton>
        <UserUpdatePasswordDialogWithButton
          email={initialValues?.email}
          firstName={initialValues?.firstName}
          lastName={initialValues?.lastName}
          formRef={formRef}
          permissionName="CAN_UPDATE_SP_PASSWORD"
        />
        <UserResetMFADialogWithButton
          email={initialValues?.email}
          firstName={initialValues?.firstName}
          lastName={initialValues?.lastName}
          formRef={formRef}
          permissionName="CAN_RESET_SP_USER_MFA"
        />
        <UserSendPasswordResetEmailDialogWithButton
          email={initialValues?.email}
          firstName={initialValues?.firstName}
          lastName={initialValues?.lastName}
          formRef={formRef}
          permissionName="CAN_SEND_SP_RESET_PASSWORD_EMAIL"
        />
      </Box>
    </Form>
  );
}

export default observer(UserEdit);
